<script>
import Icons from '@/components/Icons.vue'
export default {
	name: "AppButton",
	emits: ['click'],
	components: {
		Icons,
	},
	props: {
		variant: {
			type: String,
			default: "default",
		},
		icon: String,
		to: String,
		href: String,
		type: {
			type: String,
			default: "button",
		},
		reverse: Boolean,
		small: Boolean,
		showIcon: {
			type: Boolean,
			default: true
		},
		disabled: {
			type: Boolean,
			default: false
		},
		loading: {
			type: Boolean,
			default: false
		},
		active: {
			type: Boolean,
			default: false
		}
	},
	computed: {
		isDisabled() {
			return this.disabled || this.loading;
		},
		isExternalLink() {
			return this.href && !this.href.startsWith('/');
		},
		isInternalLink() {
			return this.to && this.to.startsWith('/');
		},
		isButton() {
			return !this.to && !this.href;
		},
		iconName() {
			return this.icon;
		},
		btnClass() {
			return [
				'AppButton',
				{ 'small': this.small },
				{ 'flex-reverse': this.reverse },
				`AppButton__${this.variant}`,
				{ 'disabled': this.disabled },
				{ 'is-loading': this.loading }
			];
		}
	},
	methods: {
		interceptAccess(event){
			if (this.loading) {
				event.preventDefault()
				return
			}
			this.$emit('click', event)
		}
	}
};
</script>

<template>
	<component
		:is="isButton ? 'button' : isExternalLink ? 'a' : 'router-link'"
		@click="interceptAccess"
		:to="isInternalLink ? to : null"
		:type="isButton ? type : null"
		:href="isExternalLink ? href : null"
		:class="btnClass"
		:disabled="isDisabled"
		:aria-busy="loading ? 'true' : null"
		:aria-current="active ? 'page' : null"
	>
		<span class="AppButton__spinner" v-if="loading" aria-hidden="true"></span>
		<Icons :name="iconName" v-else-if="showIcon" />
		<span>
			<slot></slot>
		</span>
	</component>
</template>

<!-- Options ===
<AppButton variant="primary" to="/teste" icon="addNew">Nova Campanha</AppButton>
<AppButton variant="primary">Nova Campanha</AppButton>
<AppButton variant="primary" icon="addNew">Nova Campanha</AppButton>
<AppButton variant="secondary">Label botão</AppButton>
 -->

<style lang="sass" scoped>
@use '../../assets/sass/utilities/_colors' as *

.AppButton
	--font-s: clamp(0.875rem, var(--clamp), 1rem)
	// --p: 14px
	// --pl: 30px

	--p: 12px
	--pl: 20px

	min-width: 6.25rem
	width: fit-content
	height: fit-content
	justify-content: center
	letter-spacing: -.025em
	padding: var(--p) var(--pl)
	border-radius: 99999px
	cursor: pointer !important
	font-weight: 600 !important
	display: flex 
	flex-direction: row
	align-items: center
	gap: 0.375rem	
	font-size: var(--font-s)
	white-space: nowrap
	background-color: var(--bgColor)
	color: var(--textCoolor)
	transition: all 0.23s ease
	text-decoration: none
	border: .0625rem solid var(--bdColor)
	user-select: none
	font-family: var(--lgei-text) !important

	&:disabled, &.disabled
		--bgColor: var(--color-grey-700)
		--textCoolor: var(--white)
		--bdColor: transparent
		cursor: not-allowed !important

		&:hover
			--bgColor: var(--color-grey-700)
			--textCoolor: var(--white)
			--bdColor: transparent

	&.small
		min-width: fit-content !important
		--font-s: 0.875rem
		--p: 8px
		--pl: 20px

	&.flex-reverse
		flex-direction: row-reverse

	&:not(.is-loading):has(span:empty)
		gap: 0
		padding: 10px
		border-radius: 50%	

	&:focus-visible
		outline: 0.125rem solid var(--lg-dark-gray-300)
		outline-offset: 0.125rem

	i
		width: 18px

	&__spinner
		width: 18px
		height: 18px
		flex: 0 0 18px
		border-radius: 50%
		border: 2px solid currentColor
		border-top-color: transparent
		animation: AppButtonSpin 0.6s linear infinite

	@media (prefers-reduced-motion: reduce)
		.AppButton__spinner
			animation-duration: 1.2s

	&__primary
		--bgColor: var(--lg-red-active)
		--textCoolor: var(--white)
		--bdColor: transparent

		&:hover
			--bgColor: var(--white)
			--textCoolor: var(--lg-dark-gray-200)
			--bdColor: currentcolor
		

	&__secondary
		--bgColor: var(--white)
		--textCoolor: var(--lg-dark-gray-200)
		--bdColor: currentcolor

		&:hover
			--bgColor: var(--lg-red-active)
			--textCoolor: var(--white)
			--bdColor: transparent

	&__black
		--bgColor: var(--black)
		--textCoolor: var(--white)
		--bdColor: currentcolor

		&:hover
			--bgColor: var(--lg-red-active)
			--textCoolor: var(--white)
			--bdColor: transparent

	&__profile-tab
		--bgColor: transparent
		--textCoolor: var(--lg-mid-gray-200)
		--bdColor: transparent
		min-width: auto
		padding: 0.5rem 1rem
		font-weight: 400 !important

		&[aria-current="page"]
			--bgColor: var(--lg-dark-gray-300)
			--textCoolor: var(--white)

		&:disabled, &.disabled
			--bgColor: transparent
			--textCoolor: var(--lg-mid-gray-200)
			--bdColor: transparent
			cursor: default !important

			&:hover
				--bgColor: transparent
				--textCoolor: var(--lg-mid-gray-200)
				--bdColor: transparent

		&:not([aria-current="page"]):hover
			--bgColor: var(--lg-light-gray-300)
			--textCoolor: var(--lg-dark-gray-300)
			--bdColor: transparent

	&__product-tab
		--bgColor: var(--white)
		--textCoolor: var(--lg-dark-gray-300)
		--bdColor: var(--lg-dark-gray-300)
		min-width: auto
		padding: 0.5rem 1rem
		font-family: var(--lgei-headline) !important
		font-size: 0.875rem
		font-weight: 400 !important

		&[aria-current="page"]
			--bgColor: var(--lg-dark-gray-300)
			--textCoolor: var(--white)
			--bdColor: var(--lg-dark-gray-300)

		&:hover
			--bgColor: var(--lg-dark-gray-300)
			--textCoolor: var(--white)
			--bdColor: var(--lg-dark-gray-300)

	&__product-delete
		--bgColor: var(--lg-red-active)
		--textCoolor: var(--white)
		--bdColor: transparent
		display: grid
		place-items: center
		min-width: 1rem
		width: fit-content
		height: fit-content
		padding: 4px !important
		line-height: 0

		// &:has(span:empty)
		// 	min-width: 1.5rem
		// 	width: 1.5rem
		// 	height: 1.5rem
		// 	padding: 0

		i
			display: grid
			place-items: center
			// flex: 0 0 0.75rem
			width: 0.7rem
			height: 0.7rem

		&:hover
			--bgColor: var(--lg-dark-gray-300)
			--textCoolor: var(--white)
			--bdColor: transparent

	&__password-toggle
		--bgColor: transparent
		--textCoolor: var(--lg-mid-gray-200)
		--bdColor: transparent
		position: absolute
		right: 0.75rem
		top: 50%
		translate: 0 -50%
		min-width: 1rem
		width: 1rem
		height: 1rem
		padding: 0
		border-radius: 0

		&:has(span:empty)
			min-width: 1rem
			width: 1rem
			height: 1rem
			padding: 0
			border-radius: 0

		i
			width: 1rem
			height: 1rem

		&:hover
			--bgColor: transparent
			--textCoolor: var(--lg-dark-gray-200)
			--bdColor: transparent

		&:disabled, &.disabled
			--bgColor: transparent
			--textCoolor: var(--lg-mid-gray-200)
			--bdColor: transparent
			cursor: default !important

			&:hover
				--bgColor: transparent
				--textCoolor: var(--lg-mid-gray-200)
				--bdColor: transparent

	&__photo-select
		--bgColor: var(--lg-red-active)
		--textCoolor: var(--white)
		--bdColor: transparent
		min-width: 9.25rem
		height: 2.75rem
		padding: 0.75rem 1.25rem

		&:hover
			--bgColor: var(--white)
			--textCoolor: var(--lg-dark-gray-200)
			--bdColor: currentcolor

	&__photo-overlay
		--bgColor: rgba(var(--lg-dark-gray-300-rgb), 0.6)
		--textCoolor: var(--light)
		--bdColor: transparent
		min-width: auto
		height: 2.125rem
		padding: 0.625rem 1.125rem
		font-family: var(--lgei-headline) !important
		font-size: 0.875rem
		backdrop-filter: blur(2px)

		&:hover
			--bgColor: var(--lg-dark-gray-300)

	&__profile-camera
		--bgColor: var(--lg-red-active)
		--textCoolor: var(--white)
		--bdColor: transparent
		display: grid
		place-items: center
		gap: 0
		min-width: 0
		width: 2rem
		height: 2rem
		padding: 0.4375rem
		border-radius: 50%
		line-height: 0

		i
			display: grid
			place-items: center
			width: 100%
			height: 100%

		:deep(svg)
			width: 100%
			height: 100%

		&:hover
			--bgColor: var(--lg-dark-gray-300)
	&__link
		--bgColor: transparent
		--textCoolor: var(--club--text-default)
		--bdColor: transparent
		min-width: 0
		padding: 0
		border-radius: 0
		text-decoration: underline
		text-underline-offset: .2em

		&:hover
			--bgColor: transparent
			--textCoolor: var(--lg-red-active)
			--bdColor: transparent

		&:focus-visible
			outline: 2px solid rgba(var(--lg-red-active-rgb), .35)
			outline-offset: 4px

	&__soft
		--bgColor: rgba(var(--lg-red-active-rgb), .08)
		--textCoolor: var(--lg-red-active)
		--bdColor: transparent
		font-weight: 700 !important

		&:hover
			--bgColor: rgba(var(--lg-red-active-rgb), .14)
			--textCoolor: var(--lg-red-active)
			--bdColor: transparent

		&:focus-visible
			outline: 2px solid rgba(var(--lg-red-active-rgb), .35)
			outline-offset: 3px

@keyframes AppButtonSpin
	to
		transform: rotate(360deg)
</style>
